/**
 * use-analytics-requests.ts
 *
 * Hook per l'elenco richieste usato dagli analytics:
 * - useCompletionRequests: righe delle richieste completate in un intervallo (per data
 *   completamento), sorgente dei grafici tempi di completamento e dei segmenti percentili.
 * - usePreCampionaturaRequests: righe delle richieste che hanno completato la fase di
 *   Pre-Campionatura in un intervallo, sorgente della serie "Pre-Campionatura".
 * - useAnalyticsRequests: fetch on-demand delle richieste che compongono un grafico
 *   (drill-down modal). Abilitato solo quando il modal è aperto.
 */

import { useQuery } from '@tanstack/react-query';
import { requestsNew } from '../services/api';
import { useCacheConfig } from '../contexts/cache-config-context';
import { AnalyticsRequestsQuery, RequestType } from '../types/api';

/**
 * Richieste completate (CompletedAt valorizzato) filtrate per intervallo di data
 * completamento. Sorgente unica per: barre per singola richiesta, tempo medio per
 * tipologia e segmenti percentili.
 */
export function useCompletionRequests(range: { startDate?: string; endDate?: string } = {}) {
  const { config } = useCacheConfig();

  const query: AnalyticsRequestsQuery = {
    completedOnly: true,
    dateField: 'completed',
    startDate: range.startDate,
    endDate: range.endDate,
  };

  return useQuery({
    queryKey: ['analytics-requests', 'completion', range],
    queryFn: () => requestsNew.getAnalyticsRequests(query),
    staleTime: config.requests * 2,
    gcTime: config.requests * 4,
    placeholderData: (prev) => prev,
  });
}

/**
 * Richieste di Campionatura che hanno completato la fase di Pre-Campionatura in un
 * intervallo (per data fine Pre-Campionatura). Sorgente della serie "Pre-Campionatura"
 * nella sezione tempi di completamento.
 */
export function usePreCampionaturaRequests(range: { startDate?: string; endDate?: string } = {}) {
  const { config } = useCacheConfig();

  const query: AnalyticsRequestsQuery = {
    type: RequestType.Campionatura,
    preCampionaturaCompletedOnly: true,
    dateField: 'precampionatura',
    startDate: range.startDate,
    endDate: range.endDate,
  };

  return useQuery({
    queryKey: ['analytics-requests', 'pre-campionatura', range],
    queryFn: () => requestsNew.getAnalyticsRequests(query),
    staleTime: config.requests * 2,
    gcTime: config.requests * 4,
    placeholderData: (prev) => prev,
  });
}

/**
 * Fetch on-demand delle richieste che compongono un elemento di un grafico (drill-down).
 * Passare enabled=false finché il modal non viene aperto per evitare chiamate inutili.
 */
export function useAnalyticsRequests(query: AnalyticsRequestsQuery | undefined, enabled: boolean) {
  const { config } = useCacheConfig();

  return useQuery({
    queryKey: ['analytics-requests', 'drill', query],
    queryFn: () => requestsNew.getAnalyticsRequests(query),
    enabled: enabled && !!query,
    staleTime: config.requests,
    gcTime: config.requests * 2,
  });
}
